import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookingList } from "../../store/BookingSlice";
import { RootState, AppDispatch } from "../../store/store";
import { BookingLists } from "../../interfaces/BookingList";

export default function Bookings() {
  const { t, i18n } = useTranslation();
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    dispatch(BookingList());
  }, [dispatch]);

  const programsAll = useSelector((state: RootState) => {
    return state.booking.data || ([] as BookingLists[]);
  });

  const currentLocale = i18n.language?.startsWith("fr") ? "fr-FR" : "en-US";
  const bookingsCount = Array.isArray(programsAll) ? programsAll.length : 0;

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#041B44] tracking-tight">
            {t("bookings.title", "My Bookings")}
          </h1>
        </div>
        {bookingsCount > 0 && (
          <span className="self-start sm:self-auto text-xs sm:text-sm font-bold text-[#037897] bg-[#EBF7F9] px-3.5 py-1.5 rounded-full border border-[#037897]/20">
            {t("bookings.total", "Total:")} {bookingsCount}
          </span>
        )}
      </div>

      {/* Bookings Grid */}
      {Array.isArray(programsAll) && programsAll.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {programsAll.map((booking: any) => {
            let formattedDate = "";
            if (booking?.program?.startDate) {
              try {
                formattedDate = new Intl.DateTimeFormat(currentLocale, {
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }).format(new Date(booking.program.startDate));
              } catch {
                formattedDate = booking.program.startDate;
              }
            }

            const programImage =
              booking?.program?.icon &&
              !booking.program.icon.includes("default-avatar")
                ? booking.program.icon
                : "/images/innerb.jpg";

            const bookingId = booking.id || booking._id;

            return (
              <div
                key={bookingId}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md hover:border-[#037897]/30 transition-all duration-300 flex flex-col group h-full"
              >
                {/* Program Banner Image */}
                <Link
                  to={`/booking/${bookingId}`}
                  className="h-48 w-full bg-gray-100 overflow-hidden relative block"
                >
                  <img
                    src={programImage}
                    alt={booking?.program?.title || "Program"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/images/innerb.jpg";
                    }}
                  />
                  <span className="absolute top-3 right-3 bg-white/95 backdrop-blur-xs text-[#037897] text-xs font-bold px-3 py-1 rounded-full shadow-sm border border-gray-100">
                    {t("bookings.enrolled", "Enrolled")}
                  </span>
                </Link>

                {/* Card Content Body */}
                <div className="p-5 flex-1 flex flex-col justify-between">
                  <div>
                    <Link to={`/booking/${bookingId}`} className="block mb-3">
                      <h3
                        className="text-base sm:text-lg font-bold text-[#041B44] group-hover:text-[#037897] transition-colors line-clamp-2 leading-snug"
                        title={booking?.program?.title}
                      >
                        {booking?.program?.title || "Program"}
                      </h3>
                    </Link>

                    {/* Date Information */}
                    <div className="flex items-center gap-2.5 text-xs sm:text-sm font-medium text-gray-600 bg-[#F8FAFC] px-3.5 py-2.5 rounded-xl border border-gray-100 mb-5">
                      <svg
                        className="w-4 h-4 text-[#037897] shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <div className="min-w-0 flex-1 truncate">
                        <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold block leading-none mb-0.5">
                          {t("bookings.date", "Date")}
                        </span>
                        <span className="font-semibold text-gray-700 truncate block">
                          {formattedDate || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer Button */}
                  <div>
                    <Link
                      to={`/booking/${bookingId}`}
                      className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs sm:text-sm font-bold uppercase tracking-wider bg-[#037897] hover:bg-[#025c74] text-white shadow-xs transition-all active:scale-98"
                    >
                      <span>{t("bookings.viewDetails", "View Details")}</span>
                      <svg
                        className="w-4 h-4 transition-transform group-hover:translate-x-1"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          d="M5 12h14M12 5l7 7-7 7"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Empty State */
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 sm:p-16 text-center flex flex-col items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-[#EBF7F9] text-[#037897] flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-[#041B44] mb-2">
            {t("bookings.noBookings", "No bookings found")}
          </h3>
          <p className="text-sm text-gray-500 max-w-md mb-6">
            You haven't enrolled in any healthcare programs yet. Discover our latest programs to get started.
          </p>
          <Link
            to="/programs"
            className="inline-flex items-center gap-2 bg-[#037897] hover:bg-[#025c74] text-white font-bold px-6 py-3 rounded-xl text-sm shadow-md transition-colors"
          >
            <span>{t("bookings.discoverPrograms", "Explore Programs")}</span>
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M5 12h14M12 5l7 7-7 7"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>
      )}
    </div>
  );
}